"use client"

import { useState } from "react"
import { X, Users, ArrowRight, Loader2, CheckCircle2, MessageCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { toast } from "sonner"
import axios from "@/utils/axios"

export default function CommunityModal({ isOpen, setIsOpen }) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  
  const whatsappLink = "https://chat.whatsapp.com/Ge5y0aEoMwfHlqvm2R1u3K?s=cl&p=a&ilr=0"

  const handleClose = () => {
    setIsOpen(false)
    // Optional: reset state on close if needed
    // setTimeout(() => { setIsSuccess(false); setName(""); setEmail(""); setPhone(""); }, 300)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!name || !email) {
      toast.error("Please enter your name and email address.")
      return
    }

    setIsLoading(true)

    try {
      await axios.post("/community/join", { name, email, phone_number: phone })
      
      setIsSuccess(true)
      toast.success("Successfully joined the community!")
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-xs sm:max-w-md p-0 overflow-hidden bg-card border-0 shadow-2xl rounded-2xl" aria-describedby="community-description">
        <DialogTitle className="sr-only">Join Our Community</DialogTitle>
        <DialogDescription id="community-description" className="sr-only">
          Join the Fayoz community for exclusive updates and discussions.
        </DialogDescription>
        <div className="relative">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-gray-100 rounded-full shadow-sm w-8 h-8 text-gray-500"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="flex flex-col">
            {/* Top Section - Image/Decoration */}
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center p-8 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary via-transparent to-transparent"></div>
                <div className="relative z-10 bg-white p-4 rounded-full shadow-lg">
                    <Users className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
            </div>

            {/* Bottom Section - Content */}
            <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center space-y-4 bg-white">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center text-center space-y-5 py-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-2">Welcome to the Community!</h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      We've sent you a welcome email. Click the button below to join our official WhatsApp group.
                    </p>
                  </div>
                  
                  <Button
                    asChild
                    className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-2 text-sm font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 h-12"
                  >
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="h-5 w-5" /> Join WhatsApp Group
                    </a>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center space-y-2">
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
                      Join Our Community
                    </h2>
                    <p className="text-sm text-muted-foreground font-medium">
                      Connect with others, get exclusive updates, and share your journey with us.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                      <input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number (Optional)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-2 text-sm font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 h-12"
                    >
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Join Now <ArrowRight className="h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
